import React, { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { organizationService, Organization } from '@/firebase/organizationService';
import { useNotification } from '@/contexts/NotificationContext';

interface SSOConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization: Organization | null;
}

const SSOConfigurationModal: React.FC<SSOConfigurationModalProps> = ({
  isOpen,
  onClose,
  organization,
}) => {
  const { user } = useAuth();
  const { refreshOrganization } = useOrganization();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    enabled: false,
    domain: '',
    provider: 'azure-ad' as 'azure-ad' | 'google' | 'okta' | 'custom',
    identityPlatformTenantId: '',
    oidcProviderId: '',
  });

  useEffect(() => {
    if (organization && organization.sso) {
      setFormData({
        enabled: organization.sso.enabled || false,
        domain: organization.sso.domain || '',
        provider: organization.sso.provider || 'azure-ad',
        identityPlatformTenantId: organization.sso.identityPlatformTenantId || '',
        oidcProviderId: organization.sso.oidcProviderId || '',
      });
    } else {
      setFormData({
        enabled: false,
        domain: '',
        provider: 'azure-ad',
        identityPlatformTenantId: '',
        oidcProviderId: '',
      });
    }
  }, [organization, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!organization?.id) {
      showNotification('Organization not found', 'error');
      return;
    }

    if (!user || user.id !== organization.ownerId) {
      showNotification('Only organization owners can configure SSO', 'error');
      return;
    }

    // Validation
    if (formData.enabled) {
      if (!formData.domain) {
        showNotification('Domain is required when SSO is enabled', 'error');
        return;
      }

      if (!formData.identityPlatformTenantId) {
        showNotification('Identity Platform Tenant ID is required', 'error');
        return;
      }

      if (!formData.oidcProviderId) {
        showNotification('OIDC Provider ID is required', 'error');
        return;
      }
    }

    setLoading(true);

    try {
      const ssoConfig = formData.enabled
        ? {
            enabled: true,
            domain: formData.domain.toLowerCase().trim(),
            provider: formData.provider,
            identityPlatformTenantId: formData.identityPlatformTenantId.trim(),
            oidcProviderId: formData.oidcProviderId.trim(),
            // Note: OIDC provider (issuer, client ID, secret) must be configured in Google Cloud Identity Platform tenant
          }
        : {
            enabled: false,
            // When disabled, other fields are optional
          };

      const result = await organizationService.updateOrganization(organization.id, {
        sso: ssoConfig,
      });

      if (result.success) {
        showNotification('SSO configuration updated successfully', 'success');
        await refreshOrganization();
        onClose();
      } else {
        showNotification(result.error || 'Failed to update SSO configuration', 'error');
      }
    } catch (error: any) {
      console.error('Error updating SSO configuration:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Single Sign-On (SSO) Configuration</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">About SSO</p>
                <p>
                  Enable Single Sign-On to allow users with email addresses from your domain to sign in using their organization credentials (e.g., Microsoft 360).
                  Users will be automatically redirected to your SSO provider when they enter their email on the login page.
                </p>
              </div>
            </div>
          </div>

          {/* Enable SSO */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enabled"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="enabled" className="ml-2 block text-sm font-medium text-gray-700">
              Enable Single Sign-On
            </label>
          </div>

          {formData.enabled && (
            <>
              {/* Domain */}
              <div>
                <label htmlFor="domain" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Domain <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="domain"
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  placeholder="snnmanagement.com"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                  required={formData.enabled}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Users with email addresses ending in this domain will be redirected to SSO
                </p>
              </div>

              {/* Provider */}
              <div>
                <label htmlFor="provider" className="block text-sm font-medium text-gray-700 mb-1">
                  SSO Provider Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="provider"
                  value={formData.provider}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      provider: e.target.value as 'azure-ad' | 'google' | 'okta' | 'custom',
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                  required={formData.enabled}
                >
                  <option value="azure-ad">Microsoft 360 / Azure AD (OIDC)</option>
                  <option value="google">Google Workspace (OIDC)</option>
                  <option value="okta">Okta (OIDC)</option>
                  <option value="custom">Custom OIDC Provider</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select your identity provider type. All providers use OIDC (OpenID Connect) protocol.
                </p>
              </div>

              {/* Customer Admin Instructions - Azure AD Setup */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">What Your IT Team Needs to Do (Azure AD)</h4>
                <div className="text-xs text-blue-800 space-y-2">
                  <p className="font-semibold mb-1">1. Get Azure AD Information:</p>
                  <ol className="list-decimal list-inside ml-2 space-y-1">
                    <li>Go to Azure Portal → Azure Active Directory → Overview</li>
                    <li>Copy the <strong>Tenant ID</strong> (Directory ID)</li>
                    <li>Go to App registrations → Your app (or create new one)</li>
                    <li>Copy the <strong>Application (Client) ID</strong></li>
                    <li>Go to Certificates & secrets → Create <strong>Client Secret</strong></li>
                  </ol>
                  <p className="mt-2 font-semibold mb-1">2. Configure Redirect URI in Azure AD:</p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li>Go to Azure Portal → App registrations → Your app</li>
                    <li>Navigate to "Authentication"</li>
                    <li>Add redirect URI: <code className="bg-blue-100 px-1 rounded">https://lemur-476302.firebaseapp.com/__/auth/handler</code></li>
                    <li>Save</li>
                  </ul>
                  <p className="mt-2 font-semibold mb-1">3. Provide Information to Complin Team:</p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li>Azure AD Tenant ID</li>
                    <li>Application (Client) ID</li>
                    <li>Client Secret</li>
                    <li>Email Domain (e.g., "snnmanagement.com")</li>
                  </ul>
                  <p className="mt-2 text-blue-700">
                    <strong>After Complin team configures everything, they will provide you with:</strong>
                  </p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li>Identity Platform Tenant ID (to enter below)</li>
                    <li>OIDC Provider ID (to enter below)</li>
                  </ul>
                </div>
              </div>

              {/* Identity Platform Tenant Configuration */}
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-4">
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">Identity Platform Configuration</h3>
                  <p className="text-xs text-gray-600 mt-1">
                    These values will be provided by Complin Admin after they configure the OIDC provider in Google Cloud Identity Platform.
                  </p>
                </div>
                
                {/* Identity Platform Tenant ID */}
                <div>
                  <label
                    htmlFor="identityPlatformTenantId"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Identity Platform Tenant ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="identityPlatformTenantId"
                    value={formData.identityPlatformTenantId}
                    onChange={(e) => setFormData({ ...formData, identityPlatformTenantId: e.target.value })}
                    placeholder="snn-management-mq56m"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                    required={formData.enabled}
                  />
                </div>

                {/* OIDC Provider ID */}
                <div>
                  <label
                    htmlFor="oidcProviderId"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    OIDC Provider ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="oidcProviderId"
                    value={formData.oidcProviderId}
                    onChange={(e) => setFormData({ ...formData, oidcProviderId: e.target.value })}
                    placeholder="azure-ad-snn-management"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                    required={formData.enabled}
                  />
                </div>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SSOConfigurationModal;

